import { type ServiceParams, unwrapJson } from "../client";
import type {
  ApiKeyDetail,
  ApiKeyListFilters,
  ApiKeyListResponse,
  ApiKeysWhoamiResponse,
  CreateApiKeyRequest,
  CreateApiKeyResponse,
  DisableApiKeyRequest,
  RevokeApiKeyRequest,
  RevokeApiKeyResponse,
  UpdateApiKeyRequest,
} from "./types";

/**
 * SAC-pattern service factory for ``/v1/admin/api-keys/*`` admin
 * endpoints. The list endpoint returns ``{ keys: [...] }`` wrapped —
 * the service surface unwraps to a bare array so the page code can
 * stay simple.
 */
const ApiKeys = ({ url, config }: ServiceParams) => ({
  async list(filters: ApiKeyListFilters = {}): Promise<ApiKeyDetail[]> {
    const params = new URLSearchParams();
    if (filters.status) params.set("status", filters.status);
    if (filters.workspace_id) params.set("workspace_id", filters.workspace_id);
    const qs = params.toString();
    const response = await fetch(
      `${url}/v1/admin/api-keys${qs ? `?${qs}` : ""}`,
      { method: "GET", headers: config() }
    );
    const body = await unwrapJson<ApiKeyListResponse>(response);
    return body.keys;
  },

  async get(id: string): Promise<ApiKeyDetail> {
    const response = await fetch(`${url}/v1/admin/api-keys/${id}`, {
      method: "GET",
      headers: config(),
    });
    return unwrapJson<ApiKeyDetail>(response);
  },

  async create(body: CreateApiKeyRequest): Promise<CreateApiKeyResponse> {
    const response = await fetch(`${url}/v1/admin/api-keys`, {
      method: "POST",
      headers: config(),
      body: JSON.stringify(body),
    });
    return unwrapJson<CreateApiKeyResponse>(response);
  },

  async update(
    id: string,
    body: UpdateApiKeyRequest
  ): Promise<ApiKeyDetail> {
    const response = await fetch(`${url}/v1/admin/api-keys/${id}`, {
      method: "PATCH",
      headers: config(),
      body: JSON.stringify(body),
    });
    return unwrapJson<ApiKeyDetail>(response);
  },

  async disable(
    id: string,
    body: DisableApiKeyRequest = {}
  ): Promise<ApiKeyDetail> {
    const response = await fetch(`${url}/v1/admin/api-keys/${id}/disable`, {
      method: "POST",
      headers: config(),
      body: JSON.stringify(body),
    });
    return unwrapJson<ApiKeyDetail>(response);
  },

  async enable(id: string): Promise<ApiKeyDetail> {
    const response = await fetch(`${url}/v1/admin/api-keys/${id}/enable`, {
      method: "POST",
      headers: config(),
    });
    return unwrapJson<ApiKeyDetail>(response);
  },

  async revoke(
    id: string,
    body: RevokeApiKeyRequest = {}
  ): Promise<RevokeApiKeyResponse> {
    // Backend exposes permanent revoke as DELETE /v1/admin/api-keys/{id}
    // (routes.py), not a POST /revoke sub-action like disable/enable.
    const response = await fetch(`${url}/v1/admin/api-keys/${id}`, {
      method: "DELETE",
      headers: config(),
      body: JSON.stringify(body),
    });
    return unwrapJson<RevokeApiKeyResponse>(response);
  },

  async whoami(): Promise<ApiKeysWhoamiResponse> {
    const response = await fetch(`${url}/v1/admin/api-keys/whoami`, {
      method: "GET",
      headers: config(),
    });
    return unwrapJson<ApiKeysWhoamiResponse>(response);
  },
});

export default ApiKeys;
